// Customers API layer.
//
// Mirrors the withFallback pattern from `features/reports/api.ts`: any 404/501
// from a backend endpoint that isn't wired yet resolves to an empty payload
// so the page renders its empty state rather than a red banner. Real errors
// (5xx, auth, network) still propagate so TanStack Query can surface them.
//
// Every endpoint here is the single place the rest of the admin app talks to
// customer data — do not bypass this module.

import { apiBlob, apiClient, apiClientPaginated, ApiError } from '@/lib/api-client';
import { exportQuery } from '@/lib/download-file';
import { getCurrencySymbol, getTenantCurrency } from '@/hooks/use-tenant-currency';
import type {
  Customer,
  CustomerConsentInput,
  CustomerCreateInput,
  CustomerFeedbackList,
  CustomerFeedbackSummary,
  CustomerListResult,
  CustomerPurchaseRow,
  CustomerUpdateInput,
  CustomerMergePreview,
  CommunicationLogRow,
  CustomerVoucherRedemptionRow,
  LoyaltySummary,
  RfmAnalysisData,
  RfmSegment,
  StoreCreditLedgerRow,
} from './types';

/**
 * Backend endpoints referenced by the Customer CRM feature that do not yet
 * exist — or exist only partially — in services/api. The UI treats a 404/501
 * from any of these as an empty result.
 *
 * Keep this list in sync with docs/gap-analysis-impl-vs-sdlc.md when touching.
 */
export const BACKEND_GAPS = [
  'GET  /api/v1/customers',
  'GET  /api/v1/customers/:id',
  'GET  /api/v1/customers/:id/loyalty',
  'GET  /api/v1/customers/:id/store-credit',
  'GET  /api/v1/customers/:id/communications',
  'GET  /api/v1/bills?customer_id',
  'POST /api/v1/customers',
  'PUT  /api/v1/customers/:id',
  'GET  /api/v1/customers/rfm-analysis',
  'GET  /api/v1/customers/feedback',
  'GET  /api/v1/customers/feedback/summary',
  'POST /api/v1/communications/send-invoice',
  'POST /api/v1/customers/:id/loyalty/adjust',
] as const;

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) {
      return fallback;
    }
    throw err;
  }
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}`;
}

// ─── Empty fallbacks ─────────────────────────────────────────────────────────

const emptyList: CustomerListResult = { data: [], meta: { page: 1, limit: 20, total: 0, totalPages: 0 } };

// A function, not a constant: the earn rate names the tenant's currency.
const emptyLoyalty = (): LoyaltySummary => ({
  balance: 0,
  lifetime_earned: 0,
  lifetime_redeemed: 0,
  earn_rate: `1 point per ${getCurrencySymbol(getTenantCurrency())}100`,
  expiring_points: null,
  last_earned: null,
  ledger: [],
});

const emptyRfm: RfmAnalysisData = {
  total_customers: 0,
  segments: [],
  migration_trend: [],
  period: '90d',
};

const emptyFeedbackSummary: CustomerFeedbackSummary = {
  avg_rating: 0,
  total_reviews: 0,
  positive_pct: 0,
  negative_count: 0,
  rating_distribution: [
    { rating: 1, count: 0 },
    { rating: 2, count: 0 },
    { rating: 3, count: 0 },
    { rating: 4, count: 0 },
    { rating: 5, count: 0 },
  ],
  trend: [],
};

const emptyFeedback: CustomerFeedbackList = {
  data: [],
  meta: { page: 1, limit: 20, total: 0, totalPages: 0 },
};

// ─── API ─────────────────────────────────────────────────────────────────────

export interface CustomerListParams {
  page?: number;
  limit?: number;
  search?: string;
  segment?: RfmSegment | '';
  /** RFM window for the segment filter and column — the dashboard's period (CD16); the API defaults to 90d. */
  period?: string;
  /** T27 search facets and the joined-on range. */
  name?: string;
  mobile?: string;
  email?: string;
  start_date?: string;
  end_date?: string;
  /** Status: active only when absent (the API default), 'false' = deactivated, 'all' = both (CU1-004). */
  active?: '' | 'false' | 'all';
  /** Sort key. Backend may or may not honour this yet — the loyalty
   *  dashboard relies on `loyalty_points` to surface top members. */
  sort?: 'loyalty_points' | 'total_spent' | 'last_visit' | 'created_at';
}

export interface FeedbackListParams {
  page?: number;
  limit?: number;
  rating?: number;
  location_id?: string;
  from?: string;
  to?: string;
}

/** The feedback list's filters without paging — the summary covers the same rows. */
export type FeedbackSummaryParams = Omit<FeedbackListParams, 'page' | 'limit'>;

export const customersApi = {
  async list(params: CustomerListParams): Promise<CustomerListResult> {
    return withFallback(
      async () => {
        const res = await apiClientPaginated<Customer[]>(
          `/customers${buildQuery({
            page: params.page ?? 1,
            limit: params.limit ?? 20,
            search: params.search,
            name: params.name,
            mobile: params.mobile,
            email: params.email,
            start_date: params.start_date,
            end_date: params.end_date,
            segment: params.segment,
            period: params.period,
            active: params.active,
            sort: params.sort,
          })}`,
        );
        return { data: res.data, meta: res.meta };
      },
      emptyList,
    );
  },

  /** Every customer matching the filters as CSV — the API writes it from all
   *  pages, so the export never stops at the loaded page (QA 7.1.22, T27). */
  exportCsv(params: Omit<CustomerListParams, 'page' | 'limit'>) {
    return apiBlob(`/customers/export${exportQuery(params as Record<string, string | undefined>)}`);
  },

  async get(id: string): Promise<Customer | null> {
    return withFallback(() => apiClient<Customer>(`/customers/${id}`), null);
  },

  async create(input: CustomerCreateInput): Promise<Customer> {
    return apiClient<Customer>(`/customers`, { method: 'POST', body: input });
  },

  async update(id: string, input: CustomerUpdateInput): Promise<Customer> {
    return apiClient<Customer>(`/customers/${id}`, { method: 'PUT', body: input });
  },

  /** Soft-delete: bills, points and credit are kept (CU1-004). */
  async deactivate(id: string): Promise<void> {
    await apiClient<void>(`/customers/${id}`, { method: 'DELETE' });
  },

  async reactivate(id: string): Promise<void> {
    await apiClient<void>(`/customers/${id}/reactivate`, { method: 'POST' });
  },

  /** Set marketing opt-outs (CU4-003). */
  async setConsent(id: string, input: CustomerConsentInput): Promise<void> {
    await apiClient<void>(`/customers/${id}/consent`, { method: 'PATCH', body: { ...input, source: 'ADMIN' } });
  },

  /** What merging `id` into `into` would move, and any refusal (CU1-005). */
  async mergePreview(id: string, into: string): Promise<CustomerMergePreview> {
    return apiClient<CustomerMergePreview>(`/customers/${id}/merge-preview${buildQuery({ into })}`);
  },

  /** Irreversible: everything moves to `into` and `id` is deactivated (CU1-005). */
  async mergeInto(id: string, into: string): Promise<void> {
    await apiClient<void>(`/customers/${id}/merge`, { method: 'POST', body: { into } });
  },

  async loyalty(id: string): Promise<LoyaltySummary> {
    return withFallback(() => apiClient<LoyaltySummary>(`/customers/${id}/loyalty`), emptyLoyalty());
  },

  async adjustLoyaltyPoints(id: string, amount: number, reason: string): Promise<LoyaltySummary> {
    return apiClient<LoyaltySummary>(`/customers/${id}/loyalty/adjust`, {
      method: 'POST',
      body: { amount, reason },
    });
  },

  async purchases(id: string, page = 1): Promise<{ data: CustomerPurchaseRow[]; total: number }> {
    return withFallback(
      async () => {
        const res = await apiClientPaginated<CustomerPurchaseRow[]>(
          `/bills${buildQuery({ customer_id: id, page, limit: 20 })}`,
        );
        return { data: res.data, total: res.meta.total };
      },
      { data: [], total: 0 },
    );
  },

  async storeCreditLedger(id: string): Promise<StoreCreditLedgerRow[]> {
    return withFallback(
      () => apiClient<StoreCreditLedgerRow[]>(`/customers/${id}/store-credit`),
      [],
    );
  },

  async voucherRedemptions(id: string): Promise<{ data: CustomerVoucherRedemptionRow[]; total: number }> {
    const res = await apiClientPaginated<CustomerVoucherRedemptionRow[]>(
      `/vouchers/redemptions${buildQuery({ customer_id: id, page: 1, limit: 100 })}`,
    );
    return { data: res.data, total: res.meta.total };
  },

  async communications(id: string): Promise<CommunicationLogRow[]> {
    return withFallback(
      () => apiClient<CommunicationLogRow[]>(`/customers/${id}/communications`),
      [],
    );
  },

  async sendInvoice(customerId: string, billId: string, channels: ('WHATSAPP' | 'EMAIL')[]): Promise<void> {
    await apiClient<void>(`/communications/send-invoice`, {
      method: 'POST',
      body: { customer_id: customerId, bill_id: billId, channels },
    });
  },

  async rfmAnalysis(period: RfmAnalysisData['period']): Promise<RfmAnalysisData> {
    return withFallback(
      () => apiClient<RfmAnalysisData>(`/customers/rfm-analysis${buildQuery({ period })}`),
      { ...emptyRfm, period },
    );
  },

  async feedbackSummary(params: FeedbackSummaryParams = {}): Promise<CustomerFeedbackSummary> {
    return withFallback(
      () =>
        apiClient<CustomerFeedbackSummary>(
          `/customers/feedback/summary${buildQuery({
            rating: params.rating,
            location_id: params.location_id,
            from: params.from,
            to: params.to,
          })}`,
        ),
      emptyFeedbackSummary,
    );
  },

  async feedbackList(params: FeedbackListParams): Promise<CustomerFeedbackList> {
    return withFallback(
      async () => {
        const res = await apiClientPaginated<CustomerFeedbackList['data']>(
          `/customers/feedback${buildQuery({
            page: params.page ?? 1,
            limit: params.limit ?? 20,
            rating: params.rating,
            location_id: params.location_id,
            from: params.from,
            to: params.to,
          })}`,
        );
        return { data: res.data, meta: res.meta };
      },
      emptyFeedback,
    );
  },
};
